import { Button, Stack, Alert, FormControl, Typography } from "@mui/material";
import { Automatic20Px, ConfirmationModal } from "@qubit/autoparts";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import envConstants from "~/config/envConstants";
import { useGridSelector } from "~/features/inventory/useGridSelector";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import {
  useGetAutostoreGridsQuery,
  useLazySyncGridStateQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

/**
 * Renders a dropdown to choose a grid, and a button to synchronize the state of the grid against the log publisher.
 *
 * Users may execute this during the initial setup of a new grid in Qubit.
 * @constructor
 */
export function GridStateSynchronizer() {
  const { t } = useTranslation();
  const [
    syncGridState,
    {
      isFetching: syncGridStateLoading,
      error: syncGridStateError,
      data: syncGridStateData
    }
  ] = useLazySyncGridStateQuery();

  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);

  const { data: autostoreGrids } = useGetAutostoreGridsQuery(undefined, {
    skip: !userFc
  });

  const [isConfirmationModalOpen, setConfirmationModalVisibilityStatus] =
    useState(false);

  const isAdmin = useAppSelector(selectUserIsAdmin);

  const { autostoreGridOverride: selectedAutostoreGridId, gridSelectorButton } =
    useGridSelector({ id: "grid-selector" });

  const selectedAutostoreGridName = useMemo(() => {
    return (
      autostoreGrids?.find(
        (el) => el.autostoreGridId === selectedAutostoreGridId
      )?.autostoreGridName || ""
    );
  }, [autostoreGrids, selectedAutostoreGridId]);

  const handleSyncGridState = async () => {
    if (!selectedAutostoreGridId) {
      return;
    }
    setConfirmationModalVisibilityStatus(true);

    await syncGridState({
      gridId: selectedAutostoreGridId
    });
  };
  const syncGridDataModalText = syncGridStateLoading
    ? t("syncing grid state in progress")
    : syncGridStateError
      ? t("sync grid state error")
      : syncGridStateData;

  return (
    <>
      {isAdmin &&
        envConstants.SHOW_SYNC_GRID_STATE_BUTTON === "true" &&
        !!autostoreGrids?.length && (
          <Stack
            role="presentation"
            aria-label="Grid state synchronizer spacer"
            direction="row"
            gap={2}
            alignItems="flex-end"
          >
            <Typography variant="body1" alignSelf="center">
              {t("grid")}
            </Typography>
            <FormControl>{gridSelectorButton}</FormControl>
            <Button
              variant="subtle"
              startIcon={<Automatic20Px fill="currentColor" />}
              onClick={handleSyncGridState}
              disabled={!selectedAutostoreGridId}
            >
              {t("sync grid state")}
            </Button>
          </Stack>
        )}
      {syncGridStateError && !syncGridStateLoading && (
        <Alert
          variant="outlined"
          severity="error"
          action={<Button onClick={handleSyncGridState}>{t("retry")}</Button>}
          sx={{
            margin: "15px 10px 5px 10px"
          }}
        >
          {t("sync grid state")}: {getMessageFromRtkError(syncGridStateError)}
        </Alert>
      )}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        confirmCb={() => {
          setConfirmationModalVisibilityStatus(false);
        }}
        closeCb={() => {
          setConfirmationModalVisibilityStatus(false);
        }}
        modalTitle={
          selectedAutostoreGridName
            ? `${t("sync grid state")} - ${selectedAutostoreGridName}`
            : t("sync grid state")
        }
        modalText={syncGridDataModalText}
        noCancelButton
        loader={syncGridStateLoading}
        disableConfirmButton={syncGridStateLoading}
        confirmText={t("close")}
        labelledById="grid-state-synchronizer-modal-title"
      />
    </>
  );
}
